package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import kotlinx.coroutines.launch
import org.example.abigail.formatMillisToDate

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ReservaEfectivoView(viewModel: AdminViewModel) {
    val ciudades by viewModel.ciudades.collectAsState()
    val precios by viewModel.precios.collectAsState()
    val puntosSubidaCatalogo by viewModel.puntosSubida.collectAsState()
    val puntosSubidaInfoList by viewModel.puntosSubidaInfo.collectAsState()
    val paradasCatalogo by viewModel.paradas.collectAsState()
    val paradasInfoList by viewModel.paradasInfo.collectAsState()
    val scope = rememberCoroutineScope()

    // Correo del titular: aquí llegarán los boletos PDF con QR.
    var correoTitular by remember { mutableStateOf("") }

    var origen by remember { mutableStateOf("") }
    var destino by remember { mutableStateOf("") }
    var fechaSeleccionada by remember { mutableStateOf(org.example.abigail.obtenerFechaHoy()) }
    var horarioSeleccionado by remember { mutableStateOf("") }
    var horariosDisponibles by remember { mutableStateOf<List<String>>(emptyList()) }
    var asientosOcupados by remember { mutableStateOf<List<Int>>(emptyList()) }
    var asientosSeleccionados by remember { mutableStateOf<List<Int>>(emptyList()) }
    var buscandoHorarios by remember { mutableStateOf(false) }
    var buscandoAsientos by remember { mutableStateOf(false) }
    var mostrarMenuHorarios by remember { mutableStateOf(false) }
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState(initialDisplayMode = DisplayMode.Picker)

    // Punto de subida
    var puntoSubidaSeleccionado by remember { mutableStateOf("") }
    var expandedPuntoSubida by remember { mutableStateOf(false) }

    // Datos de pasajeros para cada asiento seleccionado
    var pasajerosEfectivo by remember { mutableStateOf<Map<Int, PasajeroInfo>>(emptyMap()) }
    var reservando by remember { mutableStateOf(false) }
    var reservaExitosa by remember { mutableStateOf(false) }

    val precioBase = precios["base"] ?: 350
    val precioMaleta = precios["maleta"] ?: 150
    val precioMascota = precios["mascota"] ?: 150
    // Paradas de bajada disponibles: las que sean del catálogo cuya ciudad coincida con el
    // destino del viaje (mismo criterio que ve el cliente al reservar).
    val paradasBajada = remember(destino, paradasCatalogo, paradasInfoList) {
        if (destino.isBlank()) paradasCatalogo
        else paradasCatalogo.filter { p ->
            val info = paradasInfoList.find { it.nombre == p }
            info == null || info.ciudad.equals(destino, ignoreCase = true) || info.ciudad.isBlank()
        }
    }
    val layoutViaje by viewModel.layoutViaje.collectAsState()
    // La capacidad real del viaje sale del layout del vehículo asignado; 13 solo es fallback
    // cuando el viaje no tiene vehículo vinculado (no debería ocurrir en producción).
    val totalCapacidad = layoutViaje?.capacidad ?: 13

    Column(
        modifier = Modifier.fillMaxSize().padding(16.dp).verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text("Reservar en Efectivo", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
        Text("Reserva asientos para clientes que pagarán en efectivo. El chofer verá si el pago está pendiente o confirmado.", color = Color.Gray, style = MaterialTheme.typography.bodySmall)

        // Selección de ruta
        Card(colors = CardDefaults.cardColors(containerColor = Color.White), shape = RoundedCornerShape(16.dp), elevation = CardDefaults.cardElevation(2.dp)) {
            Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(modifier = Modifier.size(32.dp).background(SmartVanColors.CyanAccent.copy(alpha = 0.1f), CircleShape), contentAlignment = Alignment.Center) {
                        Icon(Icons.Filled.Map, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(18.dp))
                    }
                    Spacer(modifier = Modifier.width(10.dp))
                    Text("Seleccionar Viaje", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                }

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    AdminDropdown("Origen", ciudades, origen, { origen = it }, Modifier.weight(1f))
                    AdminDropdown("Destino", ciudades, destino, { destino = it }, Modifier.weight(1f))
                }

                Box(modifier = Modifier.fillMaxWidth().clickable { showDatePicker = true }) {
                    OutlinedTextField(
                        value = fechaSeleccionada,
                        onValueChange = {},
                        readOnly = true,
                        enabled = false,
                        label = { Text("Fecha") },
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.colors(disabledTextColor = Color.Black, disabledBorderColor = Color.Gray, disabledLabelColor = Color.Gray),
                        trailingIcon = { Icon(Icons.Filled.CalendarToday, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(20.dp)) }
                    )
                }

                if (showDatePicker) {
                    DatePickerDialog(
                        onDismissRequest = { showDatePicker = false },
                        confirmButton = {
                            TextButton(onClick = {
                                datePickerState.selectedDateMillis?.let { millis ->
                                    fechaSeleccionada = formatMillisToDate(millis)
                                }
                                showDatePicker = false
                            }) { Text("Aceptar", color = SmartVanColors.AzulOscuro) }
                        },
                        dismissButton = { TextButton(onClick = { showDatePicker = false }) { Text("Cancelar") } }
                    ) { DatePicker(state = datePickerState, showModeToggle = false) }
                }

                // Buscar horarios
                Button(
                    onClick = {
                        if (origen.isNotBlank() && destino.isNotBlank()) {
                            val rutaArmada = "$origen - $destino"
                            scope.launch {
                                buscandoHorarios = true
                                val todos = viewModel.obtenerHorariosParaFecha(rutaArmada, fechaSeleccionada)
                                val fechaHoy = org.example.abigail.obtenerFechaHoy()
                                horariosDisponibles = if (fechaSeleccionada < fechaHoy) {
                                    emptyList()
                                } else if (fechaSeleccionada == fechaHoy) {
                                    val minutosAhora = org.example.abigail.obtenerMinutosActuales()
                                    todos.filter { h ->
                                        try {
                                            val partes = h.split(" ")
                                            val tiempo = partes[0].split(":")
                                            var horas = tiempo[0].toInt()
                                            val mins = tiempo[1].toInt()
                                            val amPm = partes.getOrNull(1)?.uppercase() ?: ""
                                            if (amPm == "PM" && horas < 12) horas += 12
                                            if (amPm == "AM" && horas == 12) horas = 0
                                            (horas * 60) + mins > minutosAhora
                                        } catch (_: Exception) { true }
                                    }
                                } else todos
                                horarioSeleccionado = ""
                                asientosSeleccionados = emptyList()
                                pasajerosEfectivo = emptyMap()
                                buscandoHorarios = false
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.CyanAccent),
                    enabled = origen.isNotBlank() && destino.isNotBlank()
                ) {
                    if (buscandoHorarios) {
                        CircularProgressIndicator(color = Color.White, modifier = Modifier.size(20.dp))
                    } else {
                        Text("Buscar Horarios", fontWeight = FontWeight.Bold, color = Color.White)
                    }
                }

                if (horariosDisponibles.isNotEmpty()) {
                    ExposedDropdownMenuBox(
                        expanded = mostrarMenuHorarios,
                        onExpandedChange = { mostrarMenuHorarios = it }
                    ) {
                        OutlinedTextField(
                            value = horarioSeleccionado.ifBlank { "Seleccionar horario..." },
                            onValueChange = {},
                            readOnly = true,
                            modifier = Modifier.fillMaxWidth().menuAnchor(),
                            label = { Text("Horario") },
                            trailingIcon = { Icon(Icons.Filled.ArrowDropDown, contentDescription = null) }
                        )
                        ExposedDropdownMenu(expanded = mostrarMenuHorarios, onDismissRequest = { mostrarMenuHorarios = false }) {
                            horariosDisponibles.forEach { hora ->
                                DropdownMenuItem(
                                    text = { Text(hora) },
                                    onClick = {
                                        horarioSeleccionado = hora
                                        mostrarMenuHorarios = false
                                        scope.launch {
                                            buscandoAsientos = true
                                            viewModel.cargarLayoutDeViaje("$origen - $destino", fechaSeleccionada, hora)
                                            asientosOcupados = viewModel.buscarAsientosOcupados("$origen - $destino", fechaSeleccionada, hora)
                                            asientosSeleccionados = emptyList()
                                            pasajerosEfectivo = emptyMap()
                                            buscandoAsientos = false
                                        }
                                    }
                                )
                            }
                        }
                    }

                    // Selector de punto de subida
                    if (horarioSeleccionado.isNotBlank() && puntosSubidaCatalogo.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(4.dp))
                        Text("¿Dónde lo recogemos?", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodySmall)
                        ExposedDropdownMenuBox(
                            expanded = expandedPuntoSubida,
                            onExpandedChange = { expandedPuntoSubida = it }
                        ) {
                            OutlinedTextField(
                                value = puntoSubidaSeleccionado.ifBlank { "Seleccionar punto de subida..." },
                                onValueChange = {},
                                readOnly = true,
                                modifier = Modifier.fillMaxWidth().menuAnchor(),
                                label = { Text("Punto de Subida") },
                                leadingIcon = { Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(18.dp)) },
                                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedPuntoSubida) }
                            )
                            ExposedDropdownMenu(expanded = expandedPuntoSubida, onDismissRequest = { expandedPuntoSubida = false }) {
                                puntosSubidaCatalogo.forEach { punto ->
                                    val infoParada = puntosSubidaInfoList.find { it.nombre == punto }
                                    val horaTexto = infoParada?.hora ?: ""
                                    DropdownMenuItem(
                                        text = {
                                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                                Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(14.dp))
                                                Spacer(modifier = Modifier.width(6.dp))
                                                Column(modifier = Modifier.weight(1f)) {
                                                    Text(punto, fontWeight = FontWeight.Medium, style = MaterialTheme.typography.bodyMedium)
                                                    if (horaTexto.isNotBlank()) {
                                                        Text("Sale: $horaTexto", color = SmartVanColors.CyanAccent, style = MaterialTheme.typography.labelSmall)
                                                    }
                                                }
                                                if (punto == puntoSubidaSeleccionado) {
                                                    Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(16.dp))
                                                }
                                            }
                                        },
                                        onClick = {
                                            puntoSubidaSeleccionado = punto
                                            expandedPuntoSubida = false
                                        }
                                    )
                                }
                            }
                        }
                        // Mostrar info del punto seleccionado
                        if (puntoSubidaSeleccionado.isNotBlank()) {
                            val infoSel = puntosSubidaInfoList.find { it.nombre == puntoSubidaSeleccionado }
                            if (infoSel != null) {
                                Card(
                                    colors = CardDefaults.cardColors(containerColor = Color(0xFFF0FFF0)),
                                    shape = RoundedCornerShape(8.dp),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Filled.LocationOn, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(16.dp))
                                        Spacer(modifier = Modifier.width(6.dp))
                                        Column {
                                            Text(puntoSubidaSeleccionado, fontWeight = FontWeight.Bold, color = SmartVanColors.VerdeOscuro, style = MaterialTheme.typography.bodySmall)
                                            if (infoSel.hora.isNotBlank()) Text("Hora de salida: ${infoSel.hora}", color = SmartVanColors.CyanAccent, style = MaterialTheme.typography.labelSmall)
                                            if (infoSel.ciudad.isNotBlank()) Text("Ciudad: ${infoSel.ciudad}", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Selección de asientos
        if (horarioSeleccionado.isNotBlank() && !buscandoAsientos) {
            Card(colors = CardDefaults.cardColors(containerColor = Color.White), shape = RoundedCornerShape(16.dp), elevation = CardDefaults.cardElevation(2.dp)) {
                Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(modifier = Modifier.size(32.dp).background(SmartVanColors.Naranja.copy(alpha = 0.1f), CircleShape), contentAlignment = Alignment.Center) {
                            Icon(Icons.Filled.EventSeat, contentDescription = null, tint = SmartVanColors.Naranja, modifier = Modifier.size(18.dp))
                        }
                        Spacer(modifier = Modifier.width(10.dp))
                        Text("Seleccionar Asientos", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    }

                    val disponibles = totalCapacidad - asientosOcupados.size
                    Text("$disponibles de $totalCapacidad disponibles", style = MaterialTheme.typography.bodySmall, color = Color.Gray)

                    // Layout visual del vehículo (mismo estilo que ve el cliente al reservar).
                    MapaAsientosVehiculo(
                        vehiculo = layoutViaje,
                        ocupados = asientosOcupados.toSet(),
                        seleccionados = asientosSeleccionados.toSet(),
                        onToggleAsiento = { seatId ->
                            val yaEstaba = seatId in asientosSeleccionados
                            asientosSeleccionados = if (yaEstaba) asientosSeleccionados - seatId else asientosSeleccionados + seatId
                            pasajerosEfectivo = if (yaEstaba) pasajerosEfectivo - seatId
                                else pasajerosEfectivo + (seatId to PasajeroInfo(asiento = seatId))
                        }
                    )
                }
            }
        }

        if (buscandoAsientos) {
            Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = SmartVanColors.CyanAccent)
            }
        }

        // Datos de pasajeros
        if (asientosSeleccionados.isNotEmpty()) {
            Card(colors = CardDefaults.cardColors(containerColor = Color.White), shape = RoundedCornerShape(16.dp), elevation = CardDefaults.cardElevation(2.dp)) {
                Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(modifier = Modifier.size(32.dp).background(SmartVanColors.Verde.copy(alpha = 0.1f), CircleShape), contentAlignment = Alignment.Center) {
                            Icon(Icons.Filled.Person, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(18.dp))
                        }
                        Spacer(modifier = Modifier.width(10.dp))
                        Text("Datos de Pasajeros", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    }

                    // Correo del titular (a este correo llegan los boletos PDF con QR).
                    val correoInvalido = correoTitular.isNotBlank() && !correoTitular.contains("@")
                    OutlinedTextField(
                        value = correoTitular,
                        onValueChange = { correoTitular = it },
                        label = { Text("Correo del titular (aquí llegarán los boletos)") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        isError = correoInvalido,
                        supportingText = if (correoInvalido) { { Text("Ingresa un correo válido", color = Color.Red) } } else null
                    )

                    asientosSeleccionados.sorted().forEachIndexed { idx, seatId ->
                        val esTitular = idx == 0
                        val pasajero = pasajerosEfectivo[seatId] ?: PasajeroInfo(asiento = seatId)
                        Card(
                            colors = CardDefaults.cardColors(containerColor = Color(0xFFF9FAFB)),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text(
                                    "Asiento $seatId" + if (esTitular) " · Titular" else "",
                                    fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro
                                )
                                OutlinedTextField(
                                    value = pasajero.nombre,
                                    onValueChange = { nombre ->
                                        pasajerosEfectivo = pasajerosEfectivo + (seatId to pasajero.copy(nombre = nombre))
                                    },
                                    label = { Text("Nombre del pasajero") },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true
                                )
                                OutlinedTextField(
                                    value = pasajero.telefono,
                                    onValueChange = { tel ->
                                        // takeLast(10): si el usuario pega "+521234567890", filter deja "521234567890"
                                        // y takeLast agarra "1234567890" (los 10 correctos), no los primeros que serían "5212345678".
                                        pasajerosEfectivo = pasajerosEfectivo + (seatId to pasajero.copy(telefono = tel.filter { c -> c.isDigit() }.takeLast(10)))
                                    },
                                    label = { Text("Teléfono (10 dígitos)") },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true
                                )

                                // Parada de bajada (dropdown filtrado por destino).
                                var expandedParada by remember(seatId) { mutableStateOf(false) }
                                ExposedDropdownMenuBox(
                                    expanded = expandedParada,
                                    onExpandedChange = { expandedParada = it }
                                ) {
                                    OutlinedTextField(
                                        value = pasajero.parada.ifBlank { "Selecciona bajada..." },
                                        onValueChange = {},
                                        readOnly = true,
                                        label = { Text("Baja en") },
                                        modifier = Modifier.fillMaxWidth().menuAnchor(),
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedParada) }
                                    )
                                    ExposedDropdownMenu(expanded = expandedParada, onDismissRequest = { expandedParada = false }) {
                                        paradasBajada.forEach { par ->
                                            DropdownMenuItem(
                                                text = { Text(par) },
                                                onClick = {
                                                    pasajerosEfectivo = pasajerosEfectivo + (seatId to pasajero.copy(parada = par))
                                                    expandedParada = false
                                                }
                                            )
                                        }
                                    }
                                }

                                // Maletas +/- (0..2) y mascota (switch).
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column {
                                        Text("Maletas Extras ($$precioMaleta)", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            IconButton(onClick = {
                                                if (pasajero.maletasExtras > 0)
                                                    pasajerosEfectivo = pasajerosEfectivo + (seatId to pasajero.copy(maletasExtras = pasajero.maletasExtras - 1))
                                            }) { Text("-", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro) }
                                            Text("${pasajero.maletasExtras}", fontWeight = FontWeight.Bold)
                                            IconButton(onClick = {
                                                if (pasajero.maletasExtras < 2)
                                                    pasajerosEfectivo = pasajerosEfectivo + (seatId to pasajero.copy(maletasExtras = pasajero.maletasExtras + 1))
                                            }) { Text("+", fontWeight = FontWeight.Bold, color = if (pasajero.maletasExtras < 2) SmartVanColors.AzulOscuro else Color.LightGray) }
                                        }
                                    }
                                    Column(horizontalAlignment = Alignment.End) {
                                        Text("Mascota ($$precioMascota)", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                                        Switch(
                                            checked = pasajero.llevaMascota,
                                            onCheckedChange = { pasajerosEfectivo = pasajerosEfectivo + (seatId to pasajero.copy(llevaMascota = it)) },
                                            colors = SwitchDefaults.colors(checkedThumbColor = SmartVanColors.AzulOscuro)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Total = suma por pasajero (base + maletas × precio_maleta + mascota × precio_mascota).
            val totalReserva = asientosSeleccionados.sumOf { seatId ->
                val p = pasajerosEfectivo[seatId] ?: PasajeroInfo(asiento = seatId)
                precioBase + p.maletasExtras * precioMaleta + (if (p.llevaMascota) precioMascota else 0)
            }
            Card(
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                shape = RoundedCornerShape(12.dp)
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text("Resumen de Reserva en Efectivo", fontWeight = FontWeight.Bold, color = Color(0xFF6D4C00))
                    Text("${asientosSeleccionados.size} asiento(s): ${asientosSeleccionados.sorted().joinToString(", ")}", style = MaterialTheme.typography.bodySmall, color = Color(0xFF6D4C00))
                    val totalMaletas = asientosSeleccionados.sumOf { (pasajerosEfectivo[it]?.maletasExtras) ?: 0 }
                    val totalMascotas = asientosSeleccionados.count { pasajerosEfectivo[it]?.llevaMascota == true }
                    if (totalMaletas > 0 || totalMascotas > 0) {
                        Text(
                            "Extras: $totalMaletas maleta(s), $totalMascotas mascota(s)",
                            style = MaterialTheme.typography.bodySmall, color = Color(0xFF6D4C00)
                        )
                    }
                    Text("Total a cobrar: $$totalReserva MXN", fontWeight = FontWeight.Black, color = SmartVanColors.Naranja, style = MaterialTheme.typography.titleMedium)
                    Spacer(modifier = Modifier.height(4.dp))
                    Text("El chofer verá esta reserva como \"Pago Pendiente\" hasta que se confirme.", style = MaterialTheme.typography.bodySmall, color = Color(0xFF6D4C00))
                    if (correoTitular.contains("@")) {
                        Text("Los boletos con QR se enviarán a $correoTitular", style = MaterialTheme.typography.bodySmall, color = Color(0xFF6D4C00))
                    }
                }
            }

            Button(
                onClick = {
                    reservando = true
                    // El primer asiento seleccionado (titular) recibe el correo; los demás quedan sin correo.
                    val ordenados = asientosSeleccionados.sorted()
                    val listaPasajeros = ordenados.mapIndexed { idx, seatId ->
                        val base = pasajerosEfectivo[seatId] ?: PasajeroInfo(asiento = seatId)
                        if (idx == 0) base.copy(asiento = seatId, correo = correoTitular.trim())
                        else base.copy(asiento = seatId)
                    }
                    viewModel.reservarEnEfectivo(
                        ruta = "$origen - $destino",
                        fecha = fechaSeleccionada,
                        horario = horarioSeleccionado,
                        pasajeros = listaPasajeros,
                        totalPagado = totalReserva,
                        puntoSubida = puntoSubidaSeleccionado,
                        emailTitular = correoTitular.trim()
                    ) { exito ->
                        reservando = false
                        if (exito) {
                            reservaExitosa = true
                            asientosSeleccionados = emptyList()
                            pasajerosEfectivo = emptyMap()
                            correoTitular = ""
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth().height(50.dp),
                colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro),
                enabled = !reservando &&
                    correoTitular.contains("@") &&
                    asientosSeleccionados.all { seatId ->
                        val p = pasajerosEfectivo[seatId]
                        !p?.nombre.isNullOrBlank() && (p?.telefono?.length ?: 0) == 10
                    }
            ) {
                if (reservando) {
                    CircularProgressIndicator(color = Color.White, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Reservando...", color = Color.White)
                } else {
                    Text("Crear Reserva en Efectivo", fontWeight = FontWeight.Bold, color = Color.White)
                }
            }
        }

        if (reservaExitosa) {
            AlertDialog(
                onDismissRequest = { reservaExitosa = false },
                title = { Text("¡Reserva Creada!") },
                text = { Text("Los asientos fueron reservados. El chofer verá el pago como pendiente hasta que se confirme.") },
                confirmButton = { TextButton(onClick = { reservaExitosa = false }) { Text("Aceptar", color = SmartVanColors.AzulOscuro) } }
            )
        }
    }
}
